<template>
  <div class="messages-page">
    <!-- Success Message -->
    <div
      v-if="submitted"
      class="success-message"
    >
      <div class="success-icon">
        <feather-icon
          icon="CheckCircleIcon"
          size="64"
        />
      </div>
      <h3>{{ $t('DEBTIA_THANK_YOU') }}</h3>
      <p>{{ $t('DEBTIA_MESSAGE_RECEIVED') }}</p>
      <p
        v-if="userEmail"
        class="email-notice"
      >
        {{ $t('DEBTIA_CONFIRMATION_EMAIL') }} <strong>{{ userEmail }}</strong>
      </p>
      <b-button
        variant="primary"
        class="mt-2"
        @click="resetForm"
      >
        <feather-icon
          icon="PlusIcon"
          class="mr-50"
        />
        {{ $t('DEBTIA_NEW_INQUIRY') }}
      </b-button>
    </div>

    <!-- Main content (hidden when submitted) -->
    <template v-else>
      <div class="content-wrapper">
        <!-- Header -->
        <div class="messages-header">
          <h2 class="messages-title">
            <feather-icon
              icon="MessageSquareIcon"
              size="28"
              class="mr-75"
            />
            {{ $t('DEBTIA_MESSAGES_TITLE') }}
          </h2>
          <p class="messages-subtitle">
            {{ $t('DEBTIA_MESSAGES_SUBTITLE') }}
          </p>
        </div>

        <!-- Step 1: Case Selection -->
        <div
          v-if="currentStep === 1"
          class="step-content"
        >
          <div class="step-header">
            <h5 class="step-title">
              {{ $t('DEBTIA_WHICH_CASE') }}
            </h5>
          </div>

          <b-overlay
            :show="loading"
            variant="transparent"
            opacity="0.85"
            blur="2px"
            rounded="sm"
            spinner-variant="primary"
          >
            <!-- No cases message -->
            <div
              v-if="!loading && cases.length === 0"
              class="no-cases-message"
            >
              <feather-icon
                icon="FolderIcon"
                size="48"
                class="text-muted mb-1"
              />
              <h4 class="text-muted">
                {{ $t('DEBTIA_NO_ACTIVE_CASES') }}
              </h4>
              <p class="text-muted">
                {{ $t('DEBTIA_NO_CASES_TO_CONTACT') }}
              </p>
            </div>

            <!-- Case list -->
            <div
              v-else
              class="case-selection-list"
            >
              <div
                v-for="caseItem in cases"
                :key="caseItem.id"
                class="case-selection-item"
                @click="selectCase(caseItem)"
              >
                <div class="case-info">
                  <div class="case-creditor">
                    {{ caseItem.creditorName }}
                  </div>
                  <div class="case-details">
                    <span class="case-journal">{{ $t('DEBTIA_CASE_PREFIX') }}: {{ caseItem.journalNumber }}</span>
                    <b-badge
                      :variant="getStatusVariant(caseItem.status)"
                      class="case-status-badge"
                    >
                      {{ getStatusText(caseItem.status) }}
                    </b-badge>
                  </div>
                </div>
                <div class="case-amount">
                  <span class="amount-label">{{ $t('DEBTIA_TO_PAY') }}</span>
                  <span class="amount-value">{{ formatCurrency(caseItem.totalAmount) }}</span>
                </div>
                <div class="case-select-icon">
                  <feather-icon
                    icon="ChevronRightIcon"
                    size="20"
                  />
                </div>
              </div>
            </div>
          </b-overlay>
        </div>

        <!-- Step 2: Message Category Selection -->
        <div
          v-if="currentStep === 2"
          class="step-content"
        >
          <!-- Selected case summary -->
          <div
            v-if="selectedCase && selectedCase.creditorName"
            class="selected-case-summary"
          >
            <div class="summary-label">
              {{ $t('DEBTIA_SELECTED_CASE') }}
            </div>
            <div class="summary-content">
              <div class="summary-creditor">
                {{ selectedCase.creditorName }}
              </div>
              <div class="summary-details">
                <span>{{ $t('DEBTIA_CASE_PREFIX') }}: {{ selectedCase.journalNumber }}</span>
                <b-badge
                  :variant="getStatusVariant(selectedCase.status)"
                  class="summary-status-badge"
                >
                  {{ getStatusText(selectedCase.status) }}
                </b-badge>
                <span class="summary-amount">{{ formatCurrency(selectedCase.totalAmount) }}</span>
              </div>
            </div>
          </div>

          <div class="step-header">
            <h5 class="step-title">
              {{ $t('DEBTIA_WHAT_ABOUT') }}
            </h5>
          </div>

          <!-- Message categories -->
          <div class="category-list">
            <div
              v-for="category in filteredCategories"
              :key="category.id"
              class="category-item"
              @click="selectCategory(category)"
            >
              <div class="category-icon">
                <span
                  v-if="category.customIcon === 'kr'"
                  class="currency-icon"
                >kr</span>
                <feather-icon
                  v-else
                  :icon="category.icon"
                  size="24"
                />
              </div>
              <div class="category-content">
                <div class="category-title">
                  {{ category.title }}
                </div>
                <div
                  v-if="category.description"
                  class="category-description"
                >
                  {{ category.description }}
                </div>
              </div>
              <div class="category-select-icon">
                <feather-icon
                  icon="ChevronRightIcon"
                  size="20"
                />
              </div>
            </div>
          </div>

          <!-- Step actions -->
          <div class="step-actions">
            <b-button
              variant="outline-secondary"
              @click="goToStep(1)"
            >
              <feather-icon
                icon="ArrowLeftIcon"
                class="mr-50"
              />
              {{ $t('DEBTIA_BACK') }}
            </b-button>
          </div>
        </div>

        <!-- Step 3: Message Form -->
        <div
          v-if="currentStep === 3"
          class="step-content"
        >
          <!-- Selected case summary -->
          <div class="selected-case-summary">
            <div class="summary-label">
              {{ $t('DEBTIA_SELECTED_CASE') }}
            </div>
            <div class="summary-content">
              <div class="summary-creditor">
                {{ selectedCase.creditorName }}
              </div>
              <div class="summary-details">
                <span>{{ $t('DEBTIA_CASE_PREFIX') }}: {{ selectedCase.journalNumber }}</span>
                <b-badge
                  :variant="getStatusVariant(selectedCase.status)"
                  class="summary-status-badge"
                >
                  {{ getStatusText(selectedCase.status) }}
                </b-badge>
                <span class="summary-amount">{{ formatCurrency(selectedCase.totalAmount) }}</span>
              </div>
            </div>
          </div>

          <div class="selected-category-summary">
            <div class="summary-label">
              {{ $t('DEBTIA_SUBJECT') }}
            </div>
            <div class="summary-content">
              {{ selectedCategory.title }}
            </div>
          </div>

          <!-- Category-specific forms -->
          <div class="form-container">
            <!-- Settlement Form -->
            <template v-if="selectedCategory.id === 'settlement'">
              <div class="form-section">
                <p class="form-intro">
                  {{ $t('DEBTIA_FORM_I_WANT_TO_PAY') }}
                </p>
                <b-row>
                  <b-col md="6">
                    <b-form-group :label="$t('DEBTIA_AMOUNT')">
                      <b-input-group class="input-with-suffix">
                        <b-form-input
                          v-model="formData.settlement_amount"
                          type="number"
                          min="0"
                          :placeholder="$t('DEBTIA_FORM_AMOUNT_PLACEHOLDER')"
                          :state="validateAmount(formData.settlement_amount)"
                        />
                        <template #append>
                          <b-input-group-text>kr.</b-input-group-text>
                        </template>
                      </b-input-group>
                    </b-form-group>
                  </b-col>
                  <b-col md="6">
                    <b-form-group :label="$t('DEBTIA_FORM_BY_DATE')">
                      <div class="flatpickr-wrapper">
                        <flat-pickr
                          v-model="formData.settlement_date"
                          class="form-control"
                          :config="datePickerConfig"
                          :placeholder="$t('DEBTIA_FORM_SELECT_DATE')"
                        />
                      </div>
                    </b-form-group>
                  </b-col>
                </b-row>
                <p class="form-intro mt-2">
                  {{ $t('DEBTIA_FORM_IF_ACCEPTED') }}
                </p>
                <b-row>
                  <b-col
                    v-if="!userEmail"
                    md="6"
                  >
                    <b-form-group :label="$t('DEBTIA_FORM_EMAIL')">
                      <b-form-input
                        v-model="formData.contact_email"
                        type="email"
                        placeholder="din@email.dk"
                        :state="validateEmail(formData.contact_email)"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col md="6">
                    <b-form-group :label="$t('DEBTIA_FORM_PHONE')">
                      <b-input-group class="input-with-prefix">
                        <template #prepend>
                          <b-input-group-text>+45</b-input-group-text>
                        </template>
                        <b-form-input
                          v-model="formData.contact_phone"
                          type="tel"
                          maxlength="8"
                          placeholder="12345678"
                          :state="validatePhone(formData.contact_phone)"
                        />
                      </b-input-group>
                    </b-form-group>
                  </b-col>
                </b-row>
                <div
                  v-if="userEmail"
                  class="email-display"
                >
                  <feather-icon
                    icon="MailIcon"
                    size="16"
                    class="mr-50"
                  />
                  {{ $t('DEBTIA_FORM_RECEIVE_ON') }} <strong>{{ userEmail }}</strong>
                  <router-link
                    to="/profile"
                    class="change-link"
                  >
                    {{ $t('DEBTIA_FORM_CHANGE') }}
                  </router-link>
                </div>
              </div>
            </template>

            <!-- Payment Pause Form -->
            <template v-else-if="selectedCategory.id === 'payment_pause'">
              <div class="form-section">
                <p class="form-intro">
                  {{ $t('DEBTIA_FORM_CANNOT_PAY') }}
                </p>
                <b-form-group :label="$t('DEBTIA_FORM_NEW_DATE')">
                  <div class="flatpickr-wrapper">
                    <flat-pickr
                      v-model="formData.new_payment_date"
                      class="form-control"
                      :config="datePickerConfig"
                      :placeholder="$t('DEBTIA_FORM_SELECT_DATE')"
                    />
                  </div>
                </b-form-group>
                <email-notification-field
                  v-model="formData.contact_email"
                  :user-email="userEmail"
                  class="mt-2"
                />
              </div>
            </template>

            <!-- More Info Form -->
            <template v-else-if="selectedCategory.id === 'more_info'">
              <div class="form-section">
                <p class="form-intro">
                  {{ $t('DEBTIA_FORM_WHAT_INFO') }}
                </p>
                <div class="sub-category-list">
                  <div
                    v-for="subCat in moreInfoSubCategories"
                    :key="subCat.id"
                    class="sub-category-item"
                    :class="{ 'selected': formData.more_info_type === subCat.id }"
                    @click="formData.more_info_type = subCat.id"
                  >
                    <feather-icon
                      :icon="formData.more_info_type === subCat.id ? 'CheckCircleIcon' : 'CircleIcon'"
                      size="20"
                      class="mr-75"
                    />
                    {{ subCat.title }}
                  </div>
                </div>
                <div
                  v-if="formData.more_info_type"
                  class="info-notice mt-2"
                >
                  <feather-icon
                    icon="InfoIcon"
                    class="mr-50"
                  />
                  {{ $t('DEBTIA_FORM_DOCS_READY') }}
                </div>
                <email-notification-field
                  v-model="formData.contact_email"
                  :user-email="userEmail"
                  class="mt-2"
                />
              </div>
            </template>

            <!-- FI Number Form -->
            <template v-else-if="selectedCategory.id === 'fi_number'">
              <div class="form-section">
                <div class="info-notice">
                  <feather-icon
                    icon="InfoIcon"
                    class="mr-50"
                  />
                  {{ $t('DEBTIA_FORM_FI_READY') }}
                </div>
                <email-notification-field
                  v-model="formData.contact_email"
                  :user-email="userEmail"
                  class="mt-2"
                />
              </div>
            </template>

            <!-- Power of Attorney Form -->
            <template v-else-if="selectedCategory.id === 'power_of_attorney'">
              <div class="form-section">
                <p class="form-intro">
                  {{ $t('DEBTIA_FORM_POA_INTRO') }}
                </p>
                <b-row>
                  <b-col
                    md="6"
                    lg="4"
                  >
                    <b-form-group :label="$t('DEBTIA_FORM_POA_NAME')">
                      <b-form-input
                        v-model="formData.poa_name"
                        :placeholder="$t('DEBTIA_FORM_POA_NAME_PLACEHOLDER')"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col
                    md="6"
                    lg="4"
                  >
                    <b-form-group :label="$t('DEBTIA_FORM_POA_RELATION')">
                      <b-form-input
                        v-model="formData.poa_relation"
                        :placeholder="$t('DEBTIA_FORM_POA_RELATION_PLACEHOLDER')"
                      />
                    </b-form-group>
                  </b-col>
                </b-row>
                <p class="form-intro mt-2">
                  {{ $t('DEBTIA_FORM_POA_ADDRESS_TITLE') }}
                </p>
                <b-row>
                  <b-col md="6">
                    <b-form-group :label="$t('DEBTIA_FORM_ADDRESS')">
                      <b-form-input
                        v-model="formData.poa_address"
                        :placeholder="$t('DEBTIA_FORM_ADDRESS_PLACEHOLDER')"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col
                    md="3"
                    lg="2"
                  >
                    <b-form-group :label="$t('DEBTIA_FORM_ZIP')">
                      <b-form-input
                        v-model="formData.poa_zip"
                        maxlength="4"
                        placeholder="0000"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col
                    md="3"
                    lg="2"
                  >
                    <b-form-group :label="$t('DEBTIA_FORM_CITY')">
                      <b-form-input
                        v-model="formData.poa_city"
                        :placeholder="$t('DEBTIA_FORM_CITY')"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col
                    md="3"
                    lg="2"
                  >
                    <b-form-group :label="$t('DEBTIA_FORM_COUNTRY')">
                      <b-form-input
                        :value="$t('DEBTIA_FORM_COUNTRY_VALUE')"
                        readonly
                        disabled
                      />
                    </b-form-group>
                  </b-col>
                </b-row>
                <div class="info-notice mt-2">
                  <feather-icon
                    icon="InfoIcon"
                    class="mr-50"
                  />
                  {{ $t('DEBTIA_FORM_POA_READY') }}
                </div>
                <p class="form-intro mt-2">
                  {{ $t('DEBTIA_FORM_EMAIL_READY') }}
                </p>
                <email-notification-field
                  v-model="formData.contact_email"
                  :user-email="userEmail"
                />
              </div>
            </template>

            <!-- Dispute Form -->
            <template v-else-if="selectedCategory.id === 'dispute'">
              <div class="form-section">
                <p class="form-intro">
                  {{ $t('DEBTIA_FORM_DISPUTE_WHY') }}
                </p>
                <div class="sub-category-list">
                  <div
                    v-for="subCat in disputeSubCategories"
                    :key="subCat.id"
                    class="sub-category-item"
                    :class="{ 'selected': formData.dispute_type === subCat.id }"
                    @click="selectDisputeType(subCat.id)"
                  >
                    <feather-icon
                      :icon="formData.dispute_type === subCat.id ? 'CheckCircleIcon' : 'CircleIcon'"
                      size="20"
                      class="mr-75"
                    />
                    {{ subCat.title }}
                  </div>
                </div>

                <!-- Already Paid -->
                <template v-if="formData.dispute_type === 'already_paid'">
                  <div class="sub-form mt-2">
                    <p class="form-intro">
                      {{ $t('DEBTIA_FORM_DISPUTE_RECEIPT') }}
                    </p>
                    <file-upload
                      v-model="formData.dispute_files"
                      :max-files="5"
                      accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                      :max-size="5"
                    />
                  </div>
                </template>

                <!-- Identity Theft -->
                <template v-else-if="formData.dispute_type === 'identity_theft'">
                  <div class="sub-form mt-2">
                    <div class="warning-notice">
                      <feather-icon
                        icon="AlertTriangleIcon"
                        class="mr-50"
                      />
                      {{ $t('DEBTIA_FORM_DISPUTE_POLICE') }}
                    </div>
                    <file-upload
                      v-model="formData.dispute_files"
                      :max-files="1"
                      accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                      :max-size="5"
                      class="mt-2"
                    />
                  </div>
                </template>

                <!-- General Dispute -->
                <template v-else-if="formData.dispute_type === 'general_dispute'">
                  <div class="sub-form mt-2">
                    <p class="form-intro">
                      {{ $t('DEBTIA_FORM_DISPUTE_DESCRIBE') }}
                    </p>
                    <b-form-textarea
                      v-model="formData.dispute_reason"
                      rows="4"
                      :placeholder="$t('DEBTIA_FORM_DISPUTE_PLACEHOLDER')"
                    />
                  </div>
                </template>

                <email-notification-field
                  v-if="formData.dispute_type"
                  v-model="formData.contact_email"
                  :user-email="userEmail"
                  class="mt-2"
                />
              </div>
            </template>

            <!-- Other Inquiry Form -->
            <template v-else-if="selectedCategory.id === 'other'">
              <div class="form-section">
                <div class="info-notice mb-2">
                  <feather-icon
                    icon="InfoIcon"
                    class="info-icon"
                  />
                  <span>Hvis du ikke kan finde svar under <router-link to="/faq">FAQ</router-link> eller de andre beskedmuligheder passer, kan du skrive en generel besked til os.</span>
                </div>
                <b-form-group :label="$t('DEBTIA_FORM_OTHER_LABEL')">
                  <b-form-textarea
                    v-model="formData.other_message"
                    rows="5"
                    :placeholder="$t('DEBTIA_FORM_OTHER_PLACEHOLDER')"
                  />
                </b-form-group>
                <email-notification-field
                  v-model="formData.contact_email"
                  :user-email="userEmail"
                  class="mt-2"
                />
              </div>
            </template>
          </div>

          <!-- Step actions -->
          <div class="step-actions">
            <b-button
              variant="outline-secondary"
              @click="goToStep(2)"
            >
              <feather-icon
                icon="ArrowLeftIcon"
                class="mr-50"
              />
              {{ $t('DEBTIA_BACK') }}
            </b-button>
            <b-button
              variant="primary"
              :disabled="!isFormValid || submitting"
              @click="submitForm"
            >
              <b-spinner
                v-if="submitting"
                small
                class="mr-50"
              />
              <feather-icon
                v-else
                icon="SendIcon"
                class="mr-50"
              />
              {{ submitting ? $t('DEBTIA_SENDING') : $t('DEBTIA_SEND_MESSAGE') }}
            </b-button>
          </div>

          <!-- Error message -->
          <div
            v-if="submitError"
            class="submit-error"
          >
            <feather-icon
              icon="AlertCircleIcon"
              size="16"
              class="mr-50"
            />
            {{ submitError }}
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import {
  BOverlay, BButton, BRow, BCol, BFormGroup, BFormInput,
  BInputGroup, BInputGroupText, BFormTextarea, BBadge, BSpinner,
} from 'bootstrap-vue'
import flatPickr from 'vue-flatpickr-component'
import { Danish } from 'flatpickr/dist/l10n/da'
import axios from '@axios'
import constants from '@/app-configs/constants'
import EmailNotificationField from './components/EmailNotificationField.vue'
import FileUpload from './components/FileUpload.vue'

export default {
  name: 'Messages',
  components: {
    BOverlay,
    BButton,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupText,
    BFormTextarea,
    BBadge,
    BSpinner,
    flatPickr,
    EmailNotificationField,
    FileUpload,
  },
  data() {
    return {
      loading: true,
      currentStep: 1,
      cases: [],
      selectedCase: null,
      selectedCategory: null,
      submitted: false,
      submitting: false,
      submitError: null,
      userEmail: null,
      userPhone: null,
      formData: {
        // Settlement
        settlement_amount: null,
        settlement_date: null,
        // Payment pause
        new_payment_date: null,
        // More info
        more_info_type: null,
        // Power of attorney
        poa_name: '',
        poa_relation: '',
        poa_address: '',
        poa_zip: '',
        poa_city: '',
        // Dispute
        dispute_type: null,
        dispute_files: [],
        dispute_reason: '',
        // Other
        other_message: '',
        // Common
        contact_email: '',
        contact_phone: '',
      },
      datePickerConfig: {
        dateFormat: 'd-m-Y',
        locale: Danish,
        minDate: 'today',
        maxDate: new Date().fp_incr(60), // 2 months
        disableMobile: true,
        static: true,
        position: 'auto',
      },
    }
  },
  computed: {
    categories() {
      return [
        {
          id: 'settlement',
          title: this.$t('DEBTIA_CAT_SETTLEMENT_TITLE'),
          description: this.$t('DEBTIA_CAT_SETTLEMENT_DESC'),
          customIcon: 'kr',
          requiresPaymentPlan: false,
        },
        {
          id: 'payment_pause',
          title: this.$t('DEBTIA_CAT_PAUSE_TITLE'),
          description: this.$t('DEBTIA_CAT_PAUSE_DESC'),
          icon: 'PauseCircleIcon',
          requiresPaymentPlan: true,
        },
        {
          id: 'more_info',
          title: this.$t('DEBTIA_CAT_INFO_TITLE'),
          description: this.$t('DEBTIA_CAT_INFO_DESC'),
          icon: 'InfoIcon',
          requiresPaymentPlan: false,
        },
        {
          id: 'fi_number',
          title: this.$t('DEBTIA_CAT_FI_TITLE'),
          description: this.$t('DEBTIA_CAT_FI_DESC'),
          icon: 'CreditCardIcon',
          requiresPaymentPlan: false,
        },
        {
          id: 'power_of_attorney',
          title: this.$t('DEBTIA_CAT_POA_TITLE'),
          description: this.$t('DEBTIA_CAT_POA_DESC'),
          icon: 'UsersIcon',
          requiresPaymentPlan: false,
        },
        {
          id: 'dispute',
          title: this.$t('DEBTIA_CAT_DISPUTE_TITLE'),
          description: this.$t('DEBTIA_CAT_DISPUTE_DESC'),
          icon: 'AlertTriangleIcon',
          requiresPaymentPlan: false,
        },
        {
          id: 'other',
          title: this.$t('DEBTIA_CAT_OTHER_TITLE'),
          description: this.$t('DEBTIA_CAT_OTHER_DESC'),
          icon: 'MessageCircleIcon',
          requiresPaymentPlan: false,
        },
      ]
    },
    moreInfoSubCategories() {
      return [
        { id: 'debt_overview', title: this.$t('DEBTIA_FORM_INFO_DEBT') },
        { id: 'missing_invoice', title: this.$t('DEBTIA_FORM_INFO_INVOICE') },
        { id: 'interest_overview', title: this.$t('DEBTIA_FORM_INFO_INTEREST') },
      ]
    },
    disputeSubCategories() {
      return [
        { id: 'already_paid', title: this.$t('DEBTIA_FORM_DISPUTE_PAID') },
        { id: 'identity_theft', title: this.$t('DEBTIA_FORM_DISPUTE_IDENTITY') },
        { id: 'general_dispute', title: this.$t('DEBTIA_FORM_DISPUTE_GENERAL') },
      ]
    },
    filteredCategories() {
      if (!this.selectedCase) return this.categories
      return this.categories.filter(cat => {
        if (cat.requiresPaymentPlan && !this.selectedCase.hasPaymentPlan) {
          return false
        }
        return true
      })
    },
    isFormValid() {
      if (!this.selectedCategory) return false

      switch (this.selectedCategory.id) {
        case 'settlement':
          return this.formData.settlement_amount > 0
            && this.formData.settlement_date
            && (this.userEmail || this.validateEmail(this.formData.contact_email))
            && this.validatePhone(this.formData.contact_phone)
        case 'payment_pause':
          return this.formData.new_payment_date
            && (this.userEmail || this.validateEmail(this.formData.contact_email))
        case 'more_info':
          return this.formData.more_info_type
            && (this.userEmail || this.validateEmail(this.formData.contact_email))
        case 'fi_number':
          return this.userEmail || this.validateEmail(this.formData.contact_email)
        case 'power_of_attorney':
          return this.formData.poa_name
            && this.formData.poa_address
            && this.formData.poa_zip
            && this.formData.poa_city
            && (this.userEmail || this.validateEmail(this.formData.contact_email))
        case 'dispute':
          if (!this.formData.dispute_type) return false
          if (this.formData.dispute_type === 'already_paid' || this.formData.dispute_type === 'identity_theft') {
            return this.formData.dispute_files.length > 0
              && (this.userEmail || this.validateEmail(this.formData.contact_email))
          }
          if (this.formData.dispute_type === 'general_dispute') {
            return this.formData.dispute_reason.trim().length > 0
              && (this.userEmail || this.validateEmail(this.formData.contact_email))
          }
          return false
        case 'other':
          return this.formData.other_message.trim().length > 0
            && (this.userEmail || this.validateEmail(this.formData.contact_email))
        default:
          return false
      }
    },
    messageData() {
      const baseData = {
        case_id: this.selectedCase?.id || null,
        journal_number: this.selectedCase?.journalNumber || null,
        creditor_name: this.selectedCase?.creditorName || null,
        total_amount: this.selectedCase?.totalAmount || null,
        category: this.selectedCategory?.id || null,
        category_title: this.selectedCategory?.title || null,
        contact_email: this.formData.contact_email || this.userEmail || null,
        created_at: new Date().toISOString(),
      }

      // Add category-specific data
      switch (this.selectedCategory?.id) {
        case 'settlement':
          return {
            ...baseData,
            settlement_amount: this.formData.settlement_amount,
            settlement_date: this.formData.settlement_date,
            contact_phone: this.formData.contact_phone ? `+45${this.formData.contact_phone}` : null,
          }
        case 'payment_pause':
          return {
            ...baseData,
            new_payment_date: this.formData.new_payment_date,
          }
        case 'more_info':
          return {
            ...baseData,
            info_type: this.formData.more_info_type,
          }
        case 'fi_number':
          return baseData
        case 'power_of_attorney':
          return {
            ...baseData,
            poa_name: this.formData.poa_name,
            poa_relation: this.formData.poa_relation,
            poa_address: {
              street: this.formData.poa_address,
              zip: this.formData.poa_zip,
              city: this.formData.poa_city,
              country: 'Danmark',
            },
          }
        case 'dispute':
          return {
            ...baseData,
            dispute_type: this.formData.dispute_type,
            dispute_reason: this.formData.dispute_reason || null,
            dispute_files: this.formData.dispute_files.map(f => ({
              name: f.name,
              size: f.size,
              type: f.type,
            })),
          }
        case 'other':
          return {
            ...baseData,
            message: this.formData.other_message,
          }
        default:
          return baseData
      }
    },
  },
  created() {
    this.loadUserData()
    const { caseId } = this.$route.query
    if (caseId) {
      this.fetchSingleCase(caseId)
    } else {
      this.fetchCases()
    }
  },
  methods: {
    loadUserData() {
      try {
        const userData = JSON.parse(localStorage.getItem('userData'))
        if (userData?.data) {
          this.userEmail = userData.data.email || null
          this.userPhone = userData.data.phone || null
          // Pre-fill contact fields if user has data
          if (this.userEmail) {
            this.formData.contact_email = this.userEmail
          }
          if (this.userPhone) {
            // Remove +45 prefix if present
            this.formData.contact_phone = this.userPhone.replace(/^\+?45/, '')
          }
        }
      } catch (e) {
        console.error('Error loading user data:', e)
      }
    },

    async fetchSingleCase(caseId) {
      // Go straight to step 2 with loading state
      this.currentStep = 2
      this.loading = true

      try {
        // Fetch pages until we find the case
        let page = 1
        const pageSize = 20
        let found = false

        while (!found) {
          // eslint-disable-next-line no-await-in-loop
          const response = await axios.get(`${constants.baseUrlV2}/debt-cases/list?perPage=${pageSize}&page=${page}`)

          if (response.status === 200) {
            const responseData = response.data.data
            const apiCases = responseData.data || responseData || []

            if (!apiCases || apiCases.length === 0) break

            const validCases = apiCases.filter(c => [1, 3, 4].includes(c.status))
            const mappedCases = validCases.map(caseItem => this.mapCaseData(caseItem))
            this.cases = [...this.cases, ...mappedCases]

            const numericId = Number(caseId)
            const match = mappedCases.find(c => c.id === numericId || c.id === caseId)
            if (match) {
              this.selectedCase = match
              found = true
            }

            if (apiCases.length < pageSize) break
            page += 1
          } else {
            break
          }
        }

        if (!found) {
          // Case not found, fall back to step 1
          this.currentStep = 1
        }
      } catch (error) {
        console.error('Error fetching case:', error)
        this.currentStep = 1
      } finally {
        this.loading = false
      }

      // Load remaining cases in background for step 1 (if user goes back)
      this.fetchRemainingCases()
    },

    async fetchRemainingCases() {
      // Continue fetching from where fetchSingleCase left off
      const loaded = this.cases.length
      if (loaded === 0) {
        this.fetchCases()
        return
      }

      let page = Math.ceil(loaded / 20) + 1
      const pageSize = 100

      try {
        let hasMore = true
        while (hasMore) {
          // eslint-disable-next-line no-await-in-loop
          const response = await axios.get(`${constants.baseUrlV2}/debt-cases/list?perPage=${pageSize}&page=${page}`)

          if (response.status === 200) {
            const responseData = response.data.data
            const apiCases = responseData.data || responseData || []

            if (!apiCases || apiCases.length === 0) {
              hasMore = false
              break
            }

            const validCases = apiCases.filter(c => [1, 3, 4].includes(c.status))
            const mappedCases = validCases.map(caseItem => this.mapCaseData(caseItem))
            // Only add cases we don't already have
            const existingIds = new Set(this.cases.map(c => c.id))
            const newCases = mappedCases.filter(c => !existingIds.has(c.id))
            if (newCases.length > 0) {
              this.cases = [...this.cases, ...newCases]
            }

            if (apiCases.length < pageSize) {
              hasMore = false
            } else {
              page += 1
            }
          } else {
            hasMore = false
          }
        }
      } catch (error) {
        // Silent - background loading
      }
    },

    async fetchCases() {
      this.loading = true
      this.cases = []
      let page = 1
      const pageSize = 100
      let preselected = false

      try {
        let hasMore = true
        while (hasMore) {
          // eslint-disable-next-line no-await-in-loop
          const response = await axios.get(`${constants.baseUrlV2}/debt-cases/list?perPage=${pageSize}&page=${page}`)

          if (response.status === 200) {
            const responseData = response.data.data
            const apiCases = responseData.data || responseData || []

            if (!apiCases || apiCases.length === 0) {
              hasMore = false
              break
            }

            // Exclude closed cases (status 3)
            const validCases = apiCases.filter(c => [1, 4].includes(c.status))
            const mappedCases = validCases.map(caseItem => this.mapCaseData(caseItem))
            this.cases = [...this.cases, ...mappedCases]

            if (page === 1) {
              this.loading = false
            }

            // Check for preselected case after each page
            if (!preselected) {
              preselected = this.checkPreselectedCase()
            }

            if (apiCases.length < pageSize) {
              hasMore = false
            } else {
              page += 1
            }
          } else {
            hasMore = false
          }
        }
      } catch (error) {
        console.error('Error fetching cases:', error)
      } finally {
        this.loading = false
      }
    },

    mapCaseData(caseItem) {
      const { statements = [] } = caseItem
      const excludedRows = ['0000', '9999', '99999']
      const relevantStatements = statements.filter(s => !excludedRows.includes(s.job_task_no))
      const totalAmount = relevantStatements.reduce((sum, s) => sum + (parseFloat(s.schedule_total_price) || 0), 0)
      const paidAmount = relevantStatements.reduce((sum, s) => sum + (parseFloat(s.paid_amount) || 0), 0)
      const remainingAmount = totalAmount - paidAmount

      const hasPaymentPlan = caseItem.installment_plan?.toLowerCase() === 'active'

      let status = 'active'
      if (caseItem.status === 3) {
        status = 'closed'
      } else if (hasPaymentPlan) {
        status = 'payment_plan'
      }

      return {
        id: caseItem.id,
        creditorName: caseItem.client_name || this.$t('DEBTIA_UNKNOWN_CREDITOR'),
        journalNumber: caseItem.id,
        status,
        totalAmount,
        remainingAmount,
        hasPaymentPlan,
      }
    },

    checkPreselectedCase() {
      const { caseId } = this.$route.query
      if (!caseId) return false
      const numericId = Number(caseId)
      const preselectedCase = this.cases.find(c => c.id === numericId || c.id === caseId)
      if (preselectedCase) {
        this.selectedCase = preselectedCase
        this.currentStep = 2
        return true
      }
      return false
    },

    selectCase(caseItem) {
      this.selectedCase = caseItem
      this.goToStep(2)
    },

    selectCategory(category) {
      this.selectedCategory = category
      this.resetFormData()
      this.goToStep(3)
    },

    selectDisputeType(typeId) {
      this.formData.dispute_type = typeId
      this.formData.dispute_files = []
      this.formData.dispute_reason = ''
    },

    goToStep(step) {
      this.currentStep = step
      if (step === 1) {
        this.selectedCategory = null
      }
      if (step === 2) {
        this.resetFormData()
      }
    },

    resetFormData() {
      this.formData = {
        settlement_amount: null,
        settlement_date: null,
        new_payment_date: null,
        more_info_type: null,
        poa_name: '',
        poa_relation: '',
        poa_address: '',
        poa_zip: '',
        poa_city: '',
        dispute_type: null,
        dispute_files: [],
        dispute_reason: '',
        other_message: '',
        contact_email: this.userEmail || '',
        contact_phone: this.userPhone?.replace(/^\+?45/, '') || '',
      }
    },

    resetForm() {
      this.submitted = false
      this.submitting = false
      this.submitError = null
      this.currentStep = 1
      this.selectedCase = null
      this.selectedCategory = null
      this.resetFormData()
    },

    async submitForm() {
      this.submitting = true
      this.submitError = null

      try {
        const response = await fetch('/api/send-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(this.messageData),
        })

        const result = await response.json()

        if (response.ok && result.success) {
          this.submitted = true
        } else {
          this.submitError = result.message || this.$t('DEBTIA_ERROR')
        }
      } catch (error) {
        console.error('Error sending message:', error)
        this.submitError = this.$t('DEBTIA_ERROR')
      } finally {
        this.submitting = false
      }
    },

    formatCurrency(value) {
      if (!value && value !== 0) return '—'
      return new Intl.NumberFormat('da-DK', {
        style: 'currency',
        currency: 'DKK',
        minimumFractionDigits: 2,
      }).format(value)
    },

    validateEmail(email) {
      if (!email) return null
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return re.test(email)
    },

    validatePhone(phone) {
      if (!phone) return null
      return /^\d{8}$/.test(phone)
    },

    validateAmount(amount) {
      if (amount === null || amount === '') return null
      return Number(amount) > 0
    },

    getStatusText(status) {
      const texts = {
        active: this.$t('DEBTIA_STATUS_ACTIVE'),
        hold: this.$t('DEBTIA_STATUS_HOLD'),
        payment_plan: this.$t('DEBTIA_STATUS_PAYMENT_PLAN'),
        closed: this.$t('DEBTIA_STATUS_CLOSED'),
      }
      return texts[status] || this.$t('DEBTIA_STATUS_UNKNOWN')
    },

    getStatusVariant(status) {
      const variants = {
        active: 'light-primary',
        hold: 'light-warning',
        payment_plan: 'light-info',
        closed: 'light-success',
      }
      return variants[status] || 'light-secondary'
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@core/scss/vue/libs/vue-flatpicker.scss';

.messages-page {
  .content-wrapper {
    max-width: 700px;
    margin: 0 auto;
    padding-top: 1.5rem;
  }

  .messages-header {
    margin-bottom: 2rem;

    .messages-title {
      display: flex;
      align-items: center;
      font-weight: 600;
      color: #5e5873;
      margin-bottom: 0.5rem;
    }

    .messages-subtitle {
      color: #6e6b7b;
      font-size: 1.1rem;
      margin: 0;
    }
  }

  .step-content {
    animation: fadeIn 0.3s ease;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .step-header {
    margin-bottom: 1.5rem;

    .step-title {
      font-weight: 600;
      color: #5e5873;
      margin-bottom: 0.25rem;
    }
  }

  // Case selection list
  .case-selection-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-bottom: 1.5rem;
  }

  .case-selection-item {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1.25rem;
    background: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(20px);
    border: 2px solid rgba(0, 0, 0, 0.08);
    border-radius: 0.75rem;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      border-color: rgba(8, 140, 118, 0.3);
      background: rgba(255, 255, 255, 0.9);
    }

    .case-info {
      flex: 1;
      min-width: 0;

      .case-creditor {
        font-weight: 600;
        font-size: 1.1rem;
        color: #5e5873;
        margin-bottom: 0.25rem;
      }

      .case-details {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-size: 0.9rem;
        color: #6e6b7b;

        .case-status-badge {
          font-size: 0.75rem;
          padding: 0.2rem 0.5rem;
        }
      }
    }

    .case-amount {
      text-align: right;
      flex-shrink: 0;

      .amount-label {
        display: block;
        font-size: 0.8rem;
        color: #a0a0a0;
        margin-bottom: 0.15rem;
      }

      .amount-value {
        font-weight: 600;
        font-size: 1.1rem;
        color: #ea5455;
      }
    }

    .case-select-icon {
      color: #b9b9c3;
      flex-shrink: 0;
    }
  }

  // Selected case summary
  .selected-case-summary,
  .selected-category-summary {
    padding: 1rem 1.25rem;
    background: rgba(8, 140, 118, 0.08);
    border: 1px solid rgba(8, 140, 118, 0.2);
    border-radius: 0.75rem;
    margin-bottom: 1.5rem;

    .summary-label {
      font-size: 0.8rem;
      color: #088c76;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      margin-bottom: 0.35rem;
    }

    .summary-content {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 0.5rem;
    }

    .summary-creditor {
      font-weight: 600;
      color: #5e5873;
      font-size: 1.1rem;
    }

    .summary-details {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      font-size: 0.95rem;
      color: #6e6b7b;

      .summary-status-badge {
        font-size: 0.75rem;
        padding: 0.2rem 0.5rem;
      }

      .summary-amount {
        font-weight: 600;
        color: #ea5455;
      }
    }
  }

  .selected-category-summary {
    .summary-content {
      font-weight: 500;
      color: #5e5873;
    }
  }

  // Category list
  .category-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-bottom: 1.5rem;
  }

  .category-item {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1.25rem;
    background: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(20px);
    border: 2px solid rgba(0, 0, 0, 0.08);
    border-radius: 0.75rem;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      border-color: rgba(8, 140, 118, 0.3);
      background: rgba(255, 255, 255, 0.9);
    }

    .category-icon {
      width: 48px;
      height: 48px;
      border-radius: 12px;
      background: rgba(8, 140, 118, 0.1);
      color: #088c76;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;

      .currency-icon {
        font-size: 1.1rem;
        font-weight: 700;
      }
    }

    .category-content {
      flex: 1;
      min-width: 0;

      .category-title {
        font-weight: 600;
        font-size: 1rem;
        color: #5e5873;
        margin-bottom: 0.15rem;
      }

      .category-description {
        font-size: 0.9rem;
        color: #6e6b7b;
      }
    }

    .category-select-icon {
      color: #b9b9c3;
      flex-shrink: 0;
    }
  }

  // Form container
  .form-container {
    background: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(20px);
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 0.75rem;
    padding: 2rem;
    margin-bottom: 1.5rem;
  }

  .form-section {
    .form-intro {
      font-weight: 500;
      color: #5e5873;
      margin-bottom: 1rem;
    }

    .email-display {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 0.25rem;
      padding: 1rem;
      background: rgba(8, 140, 118, 0.08);
      border: 1px solid rgba(8, 140, 118, 0.2);
      border-radius: 0.5rem;
      color: #5e5873;
      font-size: 0.95rem;
      margin-top: 1rem;

      svg {
        color: #088c76;
      }

      .change-link {
        color: #088c76;
        margin-left: 0.25rem;
        text-decoration: none;

        &:hover {
          text-decoration: underline;
        }
      }
    }
  }

  // Sub-category list
  .sub-category-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .sub-category-item {
    display: flex;
    align-items: center;
    padding: 1rem;
    background: rgba(255, 255, 255, 0.8);
    border: 2px solid rgba(0, 0, 0, 0.08);
    border-radius: 0.5rem;
    cursor: pointer;
    transition: all 0.2s ease;
    color: #5e5873;

    &:hover {
      border-color: rgba(8, 140, 118, 0.3);
    }

    &.selected {
      border-color: #088c76;
      background: rgba(8, 140, 118, 0.08);

      svg {
        color: #088c76;
      }
    }
  }

  // Notices
  .info-notice {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 1rem 1.25rem;
    background: rgba(0, 207, 232, 0.1);
    border: 1px solid rgba(0, 207, 232, 0.2);
    border-radius: 0.5rem;
    color: #00a8c7;
    font-size: 0.95rem;
    line-height: 1.5;

    .info-icon {
      flex-shrink: 0;
      margin-top: 2px;
    }

    a {
      color: #00a8c7;
      font-weight: 600;
      text-decoration: underline;

      &:hover {
        color: #008da8;
      }
    }
  }

  .warning-notice {
    display: flex;
    align-items: flex-start;
    padding: 1rem;
    background: rgba(255, 159, 67, 0.1);
    border: 1px solid rgba(255, 159, 67, 0.3);
    border-radius: 0.5rem;
    color: #ff9f43;
    font-size: 0.95rem;

    svg {
      flex-shrink: 0;
      margin-top: 2px;
    }
  }

  .sub-form {
    padding-left: 1rem;
    border-left: 3px solid #088c76;
  }

  // Step actions
  .step-actions {
    display: flex;
    justify-content: center;
    gap: 1rem;
    padding-top: 1.5rem;
    margin-top: 1.5rem;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
  }

  .submit-error {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #ea5455;
    font-size: 0.85rem;
    font-weight: 500;
    margin-top: 1rem;
    padding: 0.75rem 1rem;
    background: rgba(234, 84, 85, 0.08);
    border-radius: 0.5rem;
  }

  // No cases message
  .no-cases-message {
    text-align: center;
    padding: 4rem 2rem;
    background: rgba(255, 255, 255, 0.7);
    border-radius: 1rem;
    backdrop-filter: blur(20px);
  }

  // Success message
  .success-message {
    text-align: center;
    padding: 4rem 2rem;
    background: rgba(255, 255, 255, 0.7);
    border-radius: 1rem;
    backdrop-filter: blur(20px);
    max-width: 700px;
    margin: 0 auto;

    .success-icon {
      color: #28c76f;
      margin-bottom: 1rem;
    }

    h3 {
      color: #5e5873;
      margin-bottom: 0.5rem;
    }

    p {
      color: #6e6b7b;
      margin-bottom: 0;
    }

    .email-notice {
      margin-top: 1rem;
      padding: 0.75rem 1rem;
      background: rgba(8, 140, 118, 0.08);
      border-radius: 0.5rem;
      display: inline-block;
    }

    .btn {
      display: block;
      margin: 1.5rem auto 0;
    }
  }

  // Form field styling
  ::v-deep .form-group {
    label {
      color: #5e5873;
      font-weight: 500;
      font-size: 0.9rem;
      margin-bottom: 0.5rem;
    }
  }

  ::v-deep .form-control,
  ::v-deep .custom-select {
    background: rgba(255, 255, 255, 0.6);
    border: 1.5px solid rgba(0, 0, 0, 0.15);
    border-radius: 0.5rem;
    color: #5e5873;
    padding: 0.75rem 1rem;
    font-size: 0.95rem;
    transition: all 0.2s ease;

    &::placeholder {
      color: #a0a0a0;
    }

    &:focus {
      background: rgba(255, 255, 255, 0.9);
      border-color: #088c76;
      box-shadow: 0 0 0 3px rgba(8, 140, 118, 0.1);
    }

    &:disabled,
    &[readonly] {
      background: rgba(0, 0, 0, 0.03);
      color: #6e6b7b;
    }
  }

  ::v-deep .input-group {
    &.input-with-suffix {
      .form-control {
        border-top-right-radius: 0 !important;
        border-bottom-right-radius: 0 !important;
        border-right: none !important;
      }

      .input-group-append {
        .input-group-text {
          background: rgba(8, 140, 118, 0.1);
          border: 1.5px solid rgba(0, 0, 0, 0.15);
          border-left: none;
          border-top-right-radius: 0.5rem !important;
          border-bottom-right-radius: 0.5rem !important;
          border-top-left-radius: 0 !important;
          border-bottom-left-radius: 0 !important;
          color: #088c76;
          font-weight: 600;
          padding: 0.75rem 1rem;
          height: auto;
          display: flex;
          align-items: center;
        }
      }
    }

    &.input-with-prefix {
      .form-control {
        border-top-left-radius: 0 !important;
        border-bottom-left-radius: 0 !important;
        border-left: none !important;
      }

      .input-group-prepend {
        .input-group-text {
          background: rgba(8, 140, 118, 0.1);
          border: 1.5px solid rgba(0, 0, 0, 0.15);
          border-right: none;
          border-top-left-radius: 0.5rem !important;
          border-bottom-left-radius: 0.5rem !important;
          border-top-right-radius: 0 !important;
          border-bottom-right-radius: 0 !important;
          color: #088c76;
          font-weight: 600;
          padding: 0.75rem 1rem;
          height: auto;
          display: flex;
          align-items: center;
        }
      }
    }

    &:focus-within {
      .input-group-text {
        border-color: #088c76;
        background: rgba(8, 140, 118, 0.15);
      }
    }
  }

  // Flatpickr wrapper and styling
  .flatpickr-wrapper {
    position: relative;
    width: 100%;
  }

  ::v-deep .flatpickr-wrapper {
    width: 100%;
  }

  ::v-deep .flatpickr-input {
    background: rgba(255, 255, 255, 0.6) !important;
    border: 1.5px solid rgba(0, 0, 0, 0.15) !important;
    border-radius: 0.5rem !important;
    color: #5e5873 !important;
    padding: 0.75rem 1rem !important;
    font-size: 0.95rem !important;
    width: 100% !important;
    display: block !important;
    cursor: pointer;

    &::placeholder {
      color: #a0a0a0 !important;
    }

    &:focus {
      background: rgba(255, 255, 255, 0.9) !important;
      border-color: #088c76 !important;
      box-shadow: 0 0 0 3px rgba(8, 140, 118, 0.1) !important;
    }
  }

  ::v-deep .flatpickr-calendar {
    margin-top: 4px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    border-radius: 0.5rem;
    border: 1px solid rgba(0, 0, 0, 0.1);

    &.static {
      position: absolute;
      top: 100%;
      left: 0;
    }
  }

  // Textarea styling
  ::v-deep textarea.form-control {
    min-height: 120px;
    resize: vertical;
  }

  // Validation states
  ::v-deep .form-control.is-valid {
    border-color: #28c76f;

    &:focus {
      box-shadow: 0 0 0 3px rgba(40, 199, 111, 0.1);
    }
  }

  ::v-deep .form-control.is-invalid {
    border-color: #ea5455;

    &:focus {
      box-shadow: 0 0 0 3px rgba(234, 84, 85, 0.1);
    }
  }
}
</style>
